import React from 'react'
import PropTypes from 'prop-types'
import { classNames } from 'utils/utils'
import { equals } from 'ramda'
import {
  AH,
  AL,
  PWM,
  AUTOMATIC,
  IGNITION,
  AMPS2,
  AMPS5,
  AMPS20,
  ASAM,
  XMC,
} from 'utils/constants'
import strings from 'utils/strings'
import { RadioButtonGroup, DrawerDivider, Paragraph } from 'components/patterns'
import { SelectMenu } from 'components/chec'
import { TimerInput } from 'components/patterns/Inputs'
import { inclusiveRange } from 'utils/math'
import { virtualFuseMask } from 'i18n/displayConstants/featureOutputs'
import { TIMER_MASK_MSS } from 'constants/ui/timers'

import outputSchema from 'schemas/validation/feature/outputSchema'
import { schemaProp } from 'schemas/validation'

import PwmEditor from './PwmEditor'

import {
  StyledEditorRow,
  StyledResetAttempts,
  InfoIcon,
  AsamCurrentMessage,
} from './OutputXmcPinEditor.style'

const rule = schemaProp(outputSchema)

const OutputXmcPinEditor = ({
  className,
  polarity,
  current,
  virtualFuseSetting,
  updatePolarity,
  updateCurrent,
  resetType,
  resetAttempts,
  updateVirtualFuseSetting,
  updateResetType,
  updateResetAttempts,
  resetDelay,
  updateResetDelay,
  outputId,
  featureModuleType,
}) => {
  const isASAM = equals(featureModuleType, ASAM)
  const currentButtons = [
    {
      value: AMPS2,
      text: `${AMPS2}A`,
      disabled: isASAM,
    },
    {
      value: AMPS5,
      text: `${AMPS5}A`,
    },
    {
      value: AMPS20,
      text: `${AMPS20}A`,
      disabled: isASAM,
    },
  ]
  const polarityButtons = {
    [XMC]: [
      { value: AH, text: strings.high_side },
      {
        value: AL,
        text: strings.low_side,
        disabled: current !== AMPS2,
      },
      {
        value: PWM,
        text: strings.pwm,
        disabled: current !== AMPS2,
      },
    ],
    [ASAM]: [
      {
        value: AH,
        text: strings.high_side,
      },
      {
        value: AL,
        text: strings.low_side,
      },
    ],
  }

  return (
    <>
      <RadioButtonGroup
        vertical
        label={strings.current_a}
        onChange={updateCurrent}
        currentValue={current}
        buttonStyle="solid"
        buttons={currentButtons}
        radioGroupClassName="current-radio-group"
      />
      {isASAM && (
        <AsamCurrentMessage>
          <InfoIcon icon="alert_info_i_round_solid" fill="blackSecondary" />
          <Paragraph size="small" data-testid="current-helper-text">
            {strings.asam_current_5A_only}
          </Paragraph>
        </AsamCurrentMessage>
      )}

      <RadioButtonGroup
        vertical
        label={strings.polarity}
        onChange={updatePolarity}
        currentValue={polarity}
        buttonStyle="solid"
        buttons={polarityButtons[featureModuleType]}
        className={classNames(className, 'output-polarity-editor')}
        radioGroupClassName="polarity-radio-group"
      />

      {current === AMPS20 && (
        <TwentyAmpsEditor
          virtualFuseSetting={virtualFuseSetting}
          resetType={resetType}
          resetAttempts={resetAttempts}
          resetDelay={resetDelay}
          updateVirtualFuseSetting={updateVirtualFuseSetting}
          updateResetType={updateResetType}
          updateResetAttempts={updateResetAttempts}
          updateResetDelay={updateResetDelay}
        />
      )}

      {polarity === PWM && <PwmEditor id={outputId} />}
    </>
  )
}

OutputXmcPinEditor.defaultProps = {}

OutputXmcPinEditor.propTypes = {
  /** Make sure to comment */
  /** Classname */
  className: PropTypes.string,
  outputId: PropTypes.string,
  /** Output polarity */
  polarity: PropTypes.string,
  /** Update handler */
  updatePolarity: PropTypes.func,
  /** Output current */
  current: PropTypes.number,
  /** Output frequency */
  frequency: PropTypes.number,
  /** Output duty cycle */
  dutyCycle: PropTypes.number,
  /** Update handler */
  updateCurrent: PropTypes.func,
  /** Update handler */
  updateDutyCycle: PropTypes.func,
  /** Update handler */
  updateFrequency: PropTypes.func,
  /** Validator */
  resetType: PropTypes.string,
  /** Update handler */
  updateVirtualFuseSetting: PropTypes.func,
  /** Update handler */
  updateResetType: PropTypes.func,
  /** Update handler */
  updateResetAttempts: PropTypes.func,
  /** Output reset attemps */
  resetAttempts: PropTypes.number,
  /** Output virtual fuse setting */
  virtualFuseSetting: PropTypes.number,
  /** Output reset delay/every */
  resetDelay: PropTypes.number,
  updateResetDelay: PropTypes.func,
  featureModuleType: PropTypes.string,
}

export default OutputXmcPinEditor

const virtualFuseOptions = inclusiveRange(0, 20, 0.5)
const resetAttemptsRange = inclusiveRange(0, 15)

const resetAttemptsOptions = resetAttemptsRange.map((value) => ({
  key: value,
  title: value,
  value,
}))

const resetButtons = [
  { value: IGNITION, text: strings.ignition },
  { value: AUTOMATIC, text: strings.automatic },
]

const TwentyAmpsEditor = ({
  virtualFuseSetting,
  resetType,
  resetAttempts,
  updateVirtualFuseSetting,
  updateResetType,
  updateResetAttempts,
  resetDelay,
  updateResetDelay,
}) => {
  const isAutomatic = resetType === AUTOMATIC

  return (
    <>
      <DrawerDivider />
      <SelectMenu
        vertical
        value={virtualFuseSetting}
        label={strings.virtual_fuse_setting}
        hint={strings.virtual_fuse_setting_hint}
        onChange={updateVirtualFuseSetting}
        options={virtualFuseOptions}
        mask={virtualFuseMask}
      />

      <StyledEditorRow>
        <RadioButtonGroup
          vertical
          currentValue={resetType}
          onChange={updateResetType}
          label={strings.reset_virtual_fuse}
          buttons={resetButtons}
        />

        {isAutomatic && (
          <StyledResetAttempts
            className="form-field"
            rules={rule('resetAttempts')}
            value={resetAttempts}
            label={strings.reset_attempts}
            name={strings.reset_attempts}
            hint={strings.reset_attempts_hint}
            onChange={updateResetAttempts}
            options={resetAttemptsOptions}
          />
        )}
      </StyledEditorRow>

      {isAutomatic && (
        <TimerInput
          className="form-field"
          label={strings.reset_delay}
          hint={strings.reset_delay_hint}
          name={strings.reset_delay}
          value={resetDelay}
          onChange={updateResetDelay}
          mask={TIMER_MASK_MSS}
        />
      )}
      <DrawerDivider />
    </>
  )
}

TwentyAmpsEditor.propTypes = {
  virtualFuseSetting: PropTypes.number,
  resetType: PropTypes.string,
  resetAttempts: PropTypes.number,
  updateVirtualFuseSetting: PropTypes.func,
  updateResetType: PropTypes.func,
  updateResetAttempts: PropTypes.func,
  resetDelay: PropTypes.number,
  updateResetDelay: PropTypes.func,
}
